<template>
  <div>
    <!-- Header Row -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h5 class="m-0 font-weight-bold">Tasks</h5>

      <div class="d-flex align-items-center">
        <b-form-input
          v-model="search"
          placeholder="Search Tasks"
          class="search-input mr-2"
        ></b-form-input>

        <b-button variant="primary" class="text-nowrap" @click="openModal">
          Add Task
        </b-button>
      </div>
    </div>

    <b-table
      :items="tasks"
      :fields="fields"
      responsive
      :busy="loadingTasks"
      show-empty
      striped
      thead-class="text-nowrap"
      tbody-tr-class="text-nowrap"
    >
      <template #cell(content)="{ item }">
        <div class="d-flex align-items-center">
          <span :class="item.status === 'completed' && 'line-through'">
            {{ item.content }}
          </span>
          <b-badge
            v-if="item.status === 'completed'"
            variant="success"
            pill
            class="ml-1"
          >
            Completed
          </b-badge>
        </div>
      </template>
      <template #cell(addedBy)="{ item }">
        {{ item.firstName }} {{ item.lastName }}
      </template>
      <template #cell(dueDate)="{ item }">
        {{ format(new Date(item.dueDate), "dd/MM/yy") }}
      </template>

      <template #cell(action)="{ item }">
        <div class="d-flex">
          <b-form-checkbox
            :checked="item.status === 'completed'"
            @change="handleToggleComplete(item)"
          ></b-form-checkbox>
          <b-button variant="link" class="p-0" @click="openEditTask(item)">
            <feather-icon icon="EditIcon" />
          </b-button>
        </div>
      </template>
    </b-table>

    <div
      v-if="paginationTasks.total > 0"
      class="d-flex align-items-center justify-content-between mt-1"
    >
      <div class="text-muted small">{{ paginationSummary }}</div>
      <b-col
        cols="12"
        md="6"
        class="d-flex align-items-center justify-content-center justify-content-sm-end"
      >
        <b-pagination
          v-model="page"
          :total-rows="paginationTasks.total"
          :per-page="paginationTasks.limit"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #first-text
            ><span class="text-success">First</span></template
          >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
          <template #last-text><span class="text-info">Last</span></template>
        </b-pagination>
      </b-col>
    </div>

    <b-modal
      id="add-task-modal"
      hide-footer
      centered
      title="Add Task"
      @ok="handleAddTask"
      header-bg-variant="primary"
      @hidden="reset"
    >
      <validation-observer ref="addTask">
        <b-form-group label="To Do" label-class="font-weight-bold text-black">
          <validation-provider
            #default="{ errors }"
            name="content"
            rules="required"
          >
            <b-form-input
              v-model="form.content"
              placeholder="Enter task..."
            ></b-form-input>
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group
          label="Due Date"
          label-class="font-weight-bold text-black"
        >
          <validation-provider
            #default="{ errors }"
            name="due date"
            rules="required"
          >
            <flat-pickr v-model="form.date" :config="configDate" />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>
      </validation-observer>

      <!-- Save button -->
      <div class="text-right mt-1">
        <b-button variant="success" @click="handleAddTask"> Save </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import {
  BTable,
  BPagination,
  BFormInput,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BBadge,
  BCol,
} from "bootstrap-vue";
import { format } from "date-fns";
import flatPickr from "vue-flatpickr-component";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  components: {
    BTable,
    BPagination,
    BFormInput,
    BButton,
    BFormCheckbox,
    flatPickr,
    BFormGroup,
    ValidationProvider,
    ValidationObserver,
    BBadge,
    BCol,
  },
  data() {
    return {
      search: "",
      page: 1,
      fields: [
        { key: "content", label: "To Do" },
        { key: "addedBy", label: "Added By (Changed By)" },
        { key: "dueDate", label: "Due Date" },
        { key: "action", label: "Action" },
      ],
      tasks: [],
      form: {
        taskId: null,
        content: "",
        date: "",
      },
      configDate: {
        altFormat: "d-m-Y",
        altInput: true,
        static: true,
      },
      isEdit: false,
    };
  },
  watch: {
    page() {
      this.fetchTasks();
    },
    search() {
      this.page = 1;
      this.fetchTasks();
    },
    "$route.params.id"() {
      this.fetchTasks();
    },
  },
  created() {
    this.fetchTasks();
  },
  computed: {
    ...mapState("user/detail", ["loadingTasks", "paginationTasks"]),
    paginationSummary() {
      const p = this.paginationTasks || {};
      const page = Number(p.page) || 1;
      const limit = Number(p.limit || p.perPage) || 10;
      const total = Number(p.total) || 0;

      const from = total === 0 ? 0 : (page - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(page * limit, total);

      return total === 0
        ? `Showing 0 of 0 entries`
        : `Showing ${from} - ${to} of ${total} entries`;
    },
  },
  methods: {
    ...mapActions("user/detail", ["getTasksByUser", "addTask", "updateTask"]),
    format,
    async fetchTasks() {
      const response = await this.getTasksByUser({
        userId: this.$route.params.id,
        page: this.page,
        ...(this.search ? { search: this.search } : {}),
      });
      if (response) this.tasks = response;
    },
    openModal() {
      this.$bvModal.show("add-task-modal");
    },
    closeModal() {
      this.$bvModal.hide("add-task-modal");
      this.reset();
    },
    reset() {
      this.isEdit = false;
      this.form = {
        taskId: null,
        content: "",
        date: "",
      };
    },
    async handleAddTask() {
      const valid = await this.$refs.addTask.validate();
      if (!valid) return;
      const payload = {
        userId: this.$route.params.id,
        content: this.form.content,
        dueDate: format(new Date(this.form.date), "yyyy-MM-dd"),
        ...(this.isEdit && { taskId: this.form.taskId }),
      };
      const result = this.isEdit
        ? await this.updateTask(payload)
        : await this.addTask(payload);
      if (result) {
        this.closeModal();
        this.isEdit ? (this.tasks = result) : this.fetchTasks();
      }
    },
    async handleToggleComplete(task) {
      const result = await this.updateTask({
        taskId: task.taskId,
        status: task.status === "pending" ? "completed" : "pending",
      });
      if (result) {
        this.fetchTasks();
      }
    },
    async openEditTask(task) {
      this.isEdit = true;
      this.form = {
        taskId: task.taskId,
        content: task.content,
        date: task.dueDate,
      };
      this.$bvModal.show("add-task-modal");
    },
  },
};
</script>
<style lang="scss" scoped>
.line-through {
  text-decoration: line-through;
}
</style>
